import { computed, ref, type Ref } from 'vue';
import { useValidation } from '@/ui/concerns';
import { required, type Validator } from '@/entities/validation';
import type { IssueTemplateBuilderEditable } from '@/ui/DTO/issueTemplateBuilderEditable';
import isEmail from 'validator/lib/isEmail';
import { emptyDueAfter } from '@/ui/DTO';
import { approvalsEnabledForOrganization, hasSomePermission } from '@/ui/auth/auth';
import { actionApprovalsPermissions } from '@/ui/auth/auth.types';
import { IssuePriority } from '@/entities/issue';

export type OptVal = string | number;

export function useIssueTemplate(record: Ref<IssueTemplateBuilderEditable>) {
  const validate: Validator<IssueTemplateBuilderEditable> = model => [
    ...required(model.title.value ?? '', 'title'),
  ];

  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record);

  const removeUserPrefix = (value: string | number) => String(value).replace('user_', '');

  const isEntireDueAfterBoxOpen = (issue: IssueTemplateBuilderEditable) => {
    const due = issue.dueIn.value;
    if (due === null) {
      return false;
    }

    const KEYS = Object.keys(due) as Array<keyof typeof due>;

    if (KEYS.some(k => due[k] !== null)) {
      return true;
    }

    return issue.dueIn.required || issue.dueIn.definedByInspector;
  };

  const showEntireDueAfterBox = ref(isEntireDueAfterBoxOpen(record.value));

  const toggleDueAfterBox = (v: boolean) => {
    showEntireDueAfterBox.value = v;
    record.value.dueIn.definedByInspector = false;
    record.value.dueIn.value = v ? emptyDueAfter() : null;
    if (!v) {
      record.value.dueIn.required = false;
    }
  };

  const canSeeApprovals = () =>
    approvalsEnabledForOrganization.value && hasSomePermission(actionApprovalsPermissions);

  const isShowFormSubmitterActionCreated = computed(() => [
    record.value.title.definedByInspector,
    record.value.description.definedByInspector,
    record.value.assignees.definedByInspector,
    record.value.associateAssignees.definedByInspector,
    record.value.priority.definedByInspector,
    record.value.dueIn.definedByInspector,
    record.value.photos.definedByInspector,
  ].some(Boolean));

  const normalizeAssignees = (value: (string | number)[]) => {
    const assignees: string[] = [];
    const assignedGroups: string[] = [];
    const associateAssignees: string[] = [];

    value.forEach(v => {
      const s = String(v);
      if (isEmail(s)) {
        associateAssignees.push(removeUserPrefix(s));
      } else if (s.startsWith('user_')) {
        assignees.push(s);
      } else if (s.startsWith('group_')) {
        assignedGroups.push(s);
      }
    });

    record.value.assignees.value = assignees;
    record.value.associateAssignees.value = associateAssignees;
    record.value.assignedGroups.value = assignedGroups;
  };

  return {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,

    showEntireDueAfterBox,
    toggleDueAfterBox,

    canSeeApprovals,
    isShowFormSubmitterActionCreated,

    normalizeAssignees,
    removeUserPrefix,
    emptyDueAfter,
    IssuePriority,
  };
}
